import { useEffect, useState } from "react";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { EmptyRow, LoadingRow, Pager } from "@/components/common/States";

const PAGE_SIZE = 50;
const ENTITY_LABEL = { role: "Rol", user: "Kullanıcı", product: "Ürün", customer: "Cari", stock: "Stok", order: "Sipariş", settings: "Ayar", auth: "Oturum", backup: "Yedekleme", backup_settings: "Yedekleme ayarı", supplier_payment: "Tedarikçi Ödemesi", issued_instrument: "Verilen Çek/Senet", prim_payment: "Prim Ödemesi", supplier: "Tedarikçi" };
const FIELD_LABEL = { sale_price: "Satış fiyatı", purchase_price: "Alış fiyatı", real_cost: "Gerçek maliyet", risk_limit: "Risk limiti", vade_days: "Vade (gün)",
  physical: "Fiziki stok", balance: "Cari bakiye", min_stock: "Min. stok", min_margin_pct: "Min. sipariş marjı", status: "Sipariş durumu", "approval.status": "Onay durumu", customer_scope: "Cari görünürlüğü", sales_scope: "Satış görünürlüğü", "data_scope.customers": "Rol · Cari görünürlüğü", "data_scope.sales": "Rol · Satış görünürlüğü" };
const ACTION_STYLE = { create: "bg-emerald-50 text-emerald-700 border-emerald-200", update: "bg-blue-50 text-blue-700 border-blue-200", delete: "bg-rose-50 text-rose-700 border-rose-200", security: "bg-amber-50 text-amber-800 border-amber-200", export: "bg-slate-50 text-slate-700 border-slate-200", restore: "bg-violet-50 text-violet-700 border-violet-200" };
const ACTION_LABEL = { create: "Oluşturma", update: "Güncelleme", delete: "Silme", security: "Güvenlik", export: "Dışa Aktarma", restore: "Geri Yükleme" };

function useLabels() {
  const [schema, setSchema] = useState(null);
  useEffect(() => { api.get("/meta/permissions").then(({ data }) => setSchema(data)); }, []);
  const map = (list) => Object.fromEntries((list || []).map((x) => [x.key, x.label]));
  const mods = map(schema?.modules), acts = map(schema?.actions), flags = map(schema?.flags);
  const field = (f) => {
    if (FIELD_LABEL[f]) return FIELD_LABEL[f];
    const [head, key] = f.split(".");
    if (head === "permissions") return `Yetki · ${mods[key] || key}`;
    if (head === "flags") return `Bayrak · ${flags[key] || key}`;
    return f;
  };
  const value = (v, f) => {
    if (v === null || v === undefined) return "—";
    if (Array.isArray(v)) return v.length ? (f.startsWith("permissions.") ? v.map((a) => acts[a] || a) : v).join(", ") : "(yok)";
    if (typeof v === "boolean") return v ? "Açık" : "Kapalı";
    if (typeof v === "object") return JSON.stringify(v);
    return String(v);
  };
  return { field, value };
}

const AuditRow = ({ r, L }) => (
  <tr data-testid="audit-row">
    <td className="font-mono text-xs">{formatDate(r.timestamp)}</td>
    <td>{r.user_name}<div className="text-[11px] text-slate-400">{r.user_role}</div></td>
    <td className="font-mono text-[11px]" title={r.user_agent || ""} data-testid="audit-ip">{r.ip || "—"}{r.session_id && <div className="text-slate-400">oturum {r.session_id.slice(0, 8)}</div>}</td>
    <td>{ENTITY_LABEL[r.entity] || r.entity}<div className="text-[11px] font-mono text-slate-400">{r.entity_id}</div></td>
    <td><span className={`text-[11px] px-1.5 py-0.5 rounded border ${ACTION_STYLE[r.action]}`}>{ACTION_LABEL[r.action]}</span></td>
    <td className="font-medium text-slate-800" data-testid="audit-field">{L.field(r.field)}{r.note && <div className="text-[11px] font-normal text-slate-400 max-w-[220px] whitespace-normal" data-testid="audit-note">{r.note}</div>}</td>
    <td className="max-w-[260px] whitespace-normal"><span className="font-mono text-xs px-1.5 py-0.5 rounded bg-rose-50 text-rose-800 border border-rose-100" data-testid="audit-old-value">{L.value(r.old_value, r.field)}</span></td>
    <td className="max-w-[260px] whitespace-normal"><span className="font-mono text-xs px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-800 border border-emerald-100" data-testid="audit-new-value">{L.value(r.new_value, r.field)}</span></td>
  </tr>
);

export default function AuditLogTab() {
  const L = useLabels();
  const [q, setQ] = useState("");
  const [entity, setEntity] = useState("all");
  const [entities, setEntities] = useState([]);
  const [page, setPage] = useState(1);
  const [res, setRes] = useState(null);

  useEffect(() => { api.get("/audit-logs/entities").then(({ data }) => setEntities(data)); }, []);
  useEffect(() => {
    setRes(null);
    const t = setTimeout(() => api.get("/audit-logs", { params: { q, entity: entity === "all" ? "" : entity, page, page_size: PAGE_SIZE } }).then(({ data }) => setRes(data)), 250);
    return () => clearTimeout(t);
  }, [q, entity, page]);

  return (
    <div className="panel overflow-hidden" data-testid="audit-log-tab">
      <div className="flex flex-col sm:flex-row gap-2 p-3 border-b border-slate-200">
        <div className="relative flex-1 max-w-sm">
          <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input className="pl-8 h-9" placeholder="Alan veya kullanıcı ara..." value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} data-testid="audit-search-input" />
        </div>
        <Select value={entity} onValueChange={(v) => { setEntity(v); setPage(1); }}>
          <SelectTrigger className="h-9 sm:w-48" data-testid="audit-entity-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tüm varlıklar</SelectItem>
            {entities.map((e) => <SelectItem key={e} value={e}>{ENTITY_LABEL[e] || e}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="overflow-x-auto max-h-[calc(100vh-300px)]">
        <table className="erp-table" data-testid="audit-table">
          <thead><tr><th>Tarih</th><th>Kullanıcı</th><th>IP / Oturum</th><th>Varlık</th><th>İşlem</th><th>Alan</th><th>Eski Değer</th><th>Yeni Değer</th></tr></thead>
          <tbody>
            {!res ? <LoadingRow cols={8} /> : res.items.length === 0 ? <EmptyRow cols={8} text="Henüz denetim kaydı yok" /> : res.items.map((r) => <AuditRow key={r.id} r={r} L={L} />)}
          </tbody>
        </table>
      </div>
      {res && <Pager page={page} pageSize={PAGE_SIZE} total={res.total} onPage={setPage} testid="audit-pager" />}
    </div>
  );
}
